import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentCompliance } from './useDocuments';
import { usePersonalDocumentCompliance } from './usePersonalCompliance';
import { useClientDocumentCompliance } from './useClientDocuments';

export interface UnifiedComplianceState {
    loading: boolean;
    missingCount: number;
    expiredCount: number;
    expiringSoonCount: number;
    pendingCount: number;
    missing: string[];
    expired: string[];
    expiringSoon: string[];
    pending: string[];
}

const EMPTY_STATE: UnifiedComplianceState = Object.freeze({
    loading: false,
    missingCount: 0,
    expiredCount: 0,
    expiringSoonCount: 0,
    pendingCount: 0,
    missing: [],
    expired: [],
    expiringSoon: [],
    pending: [],
});

/**
 * Unified compliance hook — determines the current user's outstanding document
 * compliance issues (missing, expired, expiring within 30 days, pending review).
 *
 * Role resolution order:
 *  1. Platform admins → always empty (they don't own documents)
 *  2. Dual-role users → follows `active_role` to pick client OR trade path
 *  3. Client-only → client document compliance, filtered by customer business_state
 *  4. Trade admin (company:edit) → company-level + personal (field-worker) compliance
 *  5. Trade non-admin → personal compliance only (field workers)
 */
export function useUnifiedComplianceAlerts(): UnifiedComplianceState {
    const { user } = useAuth();

    // ── Role flags ──────────────────────────────────────────────────────
    const isPlatformAdmin = !!user?.is_platform_admin;
    const hasClientId = !!user?.customer_id;
    const hasTradeId = !!user?.trade_company_id;

    // Dual-role users: respect whichever role is currently active
    const activeRole = user?.active_role;
    const isActingAsClient = !isPlatformAdmin && hasClientId && (!hasTradeId || activeRole === 'customer');
    const isActingAsTrade  = !isPlatformAdmin && hasTradeId && (!hasClientId || activeRole === 'trade_company');
    const isTradeAdmin = isActingAsTrade && !!user?.permissions?.includes('company:edit');

    // ── 1. Client business_state (needed to scope client requirements) ──
    const { data: customerData, isLoading: customerDataLoading } = useQuery({
        queryKey: ['customer-business-state', user?.customer_id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('customers')
                .select('business_state')
                .eq('id', user!.customer_id!)
                .single();
            if (error) throw error;
            return data;
        },
        enabled: isActingAsClient && !!user?.customer_id,
        staleTime: 300_000,
    });

    // ── 2. Client compliance ────────────────────────────────────────────
    const { data: clientCompliance, isLoading: clientLoading } = useClientDocumentCompliance(
        isActingAsClient ? user!.customer_id! : null,
        customerData?.business_state ?? null,
    );

    // ── 3. Trade company compliance (admins only) ───────────────────────
    const { data: companyCompliance, isLoading: companyLoading } = useDocumentCompliance(
        isTradeAdmin ? user!.trade_company_id! : null,
    );

    // ── 4. Personal compliance (field workers — self-guards internally) ─
    const {
        missing: personalMissing,
        expired: personalExpired,
        expiringSoon: personalExpiring,
        pending: personalPending,
        loading: personalLoading,
    } = usePersonalDocumentCompliance();

    // Personal compliance only matters when acting as trade
    const includePersonal = isActingAsTrade;

    // ── Aggregate & memoize ─────────────────────────────────────────────
    return useMemo<UnifiedComplianceState>(() => {
        if (isPlatformAdmin || (!isActingAsClient && !isActingAsTrade)) {
            return EMPTY_STATE;
        }

        // Loading
        const loading = isActingAsClient
            ? (clientLoading || customerDataLoading)
            : isTradeAdmin
                ? (companyLoading || personalLoading)
                : personalLoading;

        if (loading) {
            return { ...EMPTY_STATE, loading: true };
        }

        // Collect raw arrays based on role
        let missing: string[];
        let expired: string[];
        let expiringSoon: string[];
        let pending: string[];

        if (isActingAsClient) {
            missing = clientCompliance?.missing ?? [];
            expired = clientCompliance?.expired ?? [];
            expiringSoon = clientCompliance?.expiringSoon ?? [];
            pending = clientCompliance?.pending ?? [];
        } else if (isTradeAdmin) {
            // Company + personal
            const cm = companyCompliance?.missing ?? [];
            const ce = companyCompliance?.expired ?? [];
            const cs = companyCompliance?.expiringSoon ?? [];
            const cp = companyCompliance?.pending ?? [];
            missing = includePersonal ? [...cm, ...personalMissing] : cm;
            expired = includePersonal ? [...ce, ...personalExpired] : ce;
            expiringSoon = includePersonal ? [...cs, ...personalExpiring] : cs;
            pending = includePersonal ? [...cp, ...personalPending] : cp;
        } else {
            // Trade non-admin: personal only
            missing = personalMissing;
            expired = personalExpired;
            expiringSoon = personalExpiring;
            pending = personalPending;
        }

        // Deduplicate
        missing = [...new Set(missing)];
        expired = [...new Set(expired)];
        expiringSoon = [...new Set(expiringSoon)];
        pending = [...new Set(pending)];

        // A document can't be both "missing" and "expired" — expired takes priority
        if (expired.length > 0) {
            const expiredSet = new Set(expired);
            missing = missing.filter(m => !expiredSet.has(m));
        }

        return {
            loading: false,
            missingCount: missing.length,
            expiredCount: expired.length,
            expiringSoonCount: expiringSoon.length,
            pendingCount: pending.length,
            missing,
            expired,
            expiringSoon,
            pending,
        };
    }, [
        isPlatformAdmin, isActingAsClient, isActingAsTrade, isTradeAdmin, includePersonal,
        clientLoading, customerDataLoading, companyLoading, personalLoading,
        clientCompliance, companyCompliance,
        personalMissing, personalExpired, personalExpiring, personalPending,
    ]);
}
